import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { containLayoutStyle } from "../../utils/videoContainCoords";
import "./poseOverlays.css";

const LEFT_COLOR = "#00BCD4";
const RIGHT_COLOR = "#FF4081";

/**
 * Confirm previously saved table corners + player ROIs before re-running pose
 * (e.g. after changing In/Out frame range).
 */
export function ConfirmCalibrationOverlay({
  tableCorners = null,
  roiLeft = null,
  roiRight = null,
  videoWidth = 0,
  videoHeight = 0,
  contentTransform = null,
  toolbarContainer = null,
  confirming = false,
  onConfirm,
  onRecalibrate,
  onCancel,
}) {
  const { t } = useTranslation();
  const stageRef = useRef(null);
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => {
      setStageSize({ w: el.clientWidth, h: el.clientHeight });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const layoutStyle = useMemo(
    () => containLayoutStyle(stageSize.w, stageSize.h, videoWidth, videoHeight),
    [stageSize, videoWidth, videoHeight]
  );

  const orientStyle = useMemo(() => {
    const base = { position: "absolute", inset: 0 };
    if (!contentTransform) return base;
    return {
      ...base,
      transform: contentTransform,
      transformOrigin: "center center",
    };
  }, [contentTransform]);

  const toolbar = (
    <div className="table-calib-overlay__toolbar">
      <p className="table-calib-overlay__hint">
        {t(
          "poseConfirmCalibHint",
          "Use the current table corners and player boxes for this frame range?"
        )}
      </p>
      <div className="table-calib-overlay__actions">
        <button
          type="button"
          className="table-calib-overlay__btn"
          onClick={onCancel}
          disabled={confirming}
        >
          {t("cancel", "Cancel")}
        </button>
        <button
          type="button"
          className="table-calib-overlay__btn"
          onClick={onRecalibrate}
          disabled={confirming}
        >
          {t("poseRecalibrate", "Recalibrate")}
        </button>
        <button
          type="button"
          className="table-calib-overlay__btn table-calib-overlay__btn--primary"
          onClick={onConfirm}
          disabled={confirming}
        >
          {confirming
            ? t("saving", "Saving…")
            : t("poseConfirmCalibRun", "Confirm & analyze")}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="table-calib-overlay table-calib-overlay--draw">
        <div ref={stageRef} className="table-calib-overlay__stage">
          <div className="table-calib-overlay__orient" style={orientStyle}>
            <div className="table-calib-overlay__video-layer" style={layoutStyle}>
              <svg
                className="table-calib-overlay__svg"
                viewBox="0 0 1 1"
                preserveAspectRatio="none"
              >
                {Array.isArray(tableCorners) && tableCorners.length === 4 ? (
                  <polygon
                    points={tableCorners.map((c) => `${c.x},${c.y}`).join(" ")}
                    fill="rgba(255,220,80,0.12)"
                    stroke="rgba(255,220,80,0.95)"
                    strokeWidth="0.004"
                  />
                ) : null}
                {roiLeft ? (
                  <rect
                    x={roiLeft.x}
                    y={roiLeft.y}
                    width={roiLeft.w}
                    height={roiLeft.h}
                    fill={`${LEFT_COLOR}22`}
                    stroke={LEFT_COLOR}
                    strokeWidth="0.006"
                  />
                ) : null}
                {roiRight ? (
                  <rect
                    x={roiRight.x}
                    y={roiRight.y}
                    width={roiRight.w}
                    height={roiRight.h}
                    fill={`${RIGHT_COLOR}22`}
                    stroke={RIGHT_COLOR}
                    strokeWidth="0.006"
                  />
                ) : null}
              </svg>
            </div>
          </div>
        </div>
      </div>
      {toolbarContainer
        ? createPortal(toolbar, toolbarContainer)
        : (
          <div className="table-calib-overlay__toolbar-fallback">
            {toolbar}
          </div>
        )}
    </>
  );
}

export default ConfirmCalibrationOverlay;
